<template>
  <main class="booking-reserve">

    <!-- 返回首頁列 -->
    <div class="br-topbar">
      <router-link to="/" class="br-back-btn">← 返回首頁</router-link>
    </div>

    <!-- 無 serviceId：引導返回 -->
    <div v-if="!serviceId" class="br-state br-state--warn">
      <p class="br-state__title">尚未選擇服務項目</p>
      <p class="br-state__msg">請先回到店家頁面選擇您想預約的服務。</p>
      <router-link
        :to="{ name: 'BookingStore', params: { id: storeId } }"
        class="br-btn br-btn--secondary"
      >
        ← 返回店家頁面
      </router-link>
    </div>

    <!-- 店家 / 服務載入中 -->
    <div v-else-if="bookingStore.storeLoading" class="br-state">
      <span class="br-spinner" />
      <p>載入中…</p>
    </div>

    <!-- 載入錯誤 -->
    <div v-else-if="bookingStore.storeError" class="br-state br-state--error">
      <p class="br-state__title">無法載入店家資料</p>
      <p class="br-state__msg">{{ bookingStore.storeError }}</p>
      <router-link :to="{ name: 'BookingHome' }" class="br-btn br-btn--secondary">
        ← 返回列表
      </router-link>
    </div>

    <!-- 服務不存在於此店家 -->
    <div v-else-if="!selectedService" class="br-state br-state--error">
      <p class="br-state__title">服務項目不存在</p>
      <p class="br-state__msg">此服務可能已下架，請重新選擇。</p>
      <router-link
        :to="{ name: 'BookingStore', params: { id: storeId } }"
        class="br-btn br-btn--secondary"
      >
        ← 返回店家頁面
      </router-link>
    </div>

    <!-- 驗證信等待畫面（提交成功但尚未點擊信件連結）-->
    <div v-else-if="pendingEmail" class="br-verify">
      <div class="br-verify__icon">
        <svg viewBox="0 0 52 42" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="1" width="50" height="40" rx="5" stroke="#8b6f5c" stroke-width="2"/>
          <path d="M1 6L26 24L51 6" stroke="#8b6f5c" stroke-width="2" stroke-linejoin="round"/>
        </svg>
      </div>
      <h1 class="br-verify__title">驗證信已寄出</h1>
      <p class="br-verify__email">{{ pendingEmailAddress }}</p>
      <p class="br-verify__msg">
        請前往您的信箱，點擊信件中的「確認完成預約」按鈕。<br/>
        連結有效時間為 <strong>30 分鐘</strong>，逾期需重新預約。
      </p>
      <div class="br-verify__spam">
        <p class="br-verify__spam-title">⚠️ 沒收到信？多半是被丟到「垃圾郵件」匣</p>
        <p class="br-verify__spam-body">
          請到信箱的<b>「垃圾郵件」或「促銷內容」</b>資料夾找找看。<br/>
          找到後請點<b>「非垃圾郵件」</b>並把我們<b>加入通訊錄</b>，之後就會正常收到。
        </p>
      </div>
      <div class="br-actions-row">
        <router-link :to="{ name: 'BookingHome' }" class="br-btn br-btn--secondary">
          返回首頁
        </router-link>
      </div>
    </div>

    <!-- 正常預約流程 -->
    <template v-else>
      <!-- 店家封面圖 -->
      <div v-if="bookingStore.currentStore?.cover_image" class="br-cover">
        <img :src="resolveImg(bookingStore.currentStore.cover_image)" :alt="bookingStore.currentStore.name" class="br-cover__img" />
        <div class="br-cover__overlay">
          <p class="br-cover__name">{{ bookingStore.currentStore.name }}</p>
        </div>
      </div>

      <!-- 麵包屑 -->
      <nav class="br-breadcrumb">
        <router-link :to="{ name: 'BookingStore', params: { id: storeId } }">← 返回店家</router-link>
      </nav>

      <h1 class="br-title">填寫預約資料</h1>

      <!-- 選定服務摘要 -->
      <div class="br-summary">
        <p class="br-summary__label">您選擇的服務</p>
        <div class="br-summary__row">
          <span class="br-summary__name">
            {{ selectedService.name }}
            <template v-if="itemName">・{{ itemName }}</template>
            <template v-else-if="selectedService.service_item">・{{ selectedService.service_item }}</template>
          </span>
          <span class="br-summary__meta">
            約 {{ effectiveDuration }} 分鐘・
            NT$ {{ Number(itemPrice ?? selectedService.price).toLocaleString() }}
          </span>
        </div>
      </div>

      <form class="br-form" @submit.prevent="submit">

        <!-- Step 1：選日期 -->
        <section class="br-section" ref="dateSectionRef">
          <h2 class="br-section-title">選擇日期</h2>
          <input
            type="date"
            v-model="selectedDate"
            :min="minDate"
            class="br-input br-input--date"
            @change="onDateChange"
          />
        </section>

        <!-- Step 2：選時段 -->
        <section class="br-section">
          <h2 class="br-section-title">選擇時段</h2>

          <div v-if="!selectedDate" class="br-hint" :class="{ 'br-hint--error': errors.slot }">
            {{ errors.slot ? '⚠️ 請先選擇日期與時段' : '請先選擇日期' }}
          </div>

          <div v-else-if="bookingStore.slotsLoading" class="br-state br-state--inline">
            <span class="br-spinner br-spinner--sm" />
            <span>載入時段中…</span>
          </div>

          <div v-else-if="bookingStore.slotsError" class="br-state br-state--inline br-state--error">
            {{ bookingStore.slotsError }}
          </div>

          <!-- 休假：後台根本沒排時段 -->
          <div v-else-if="bookingStore.totalSlots === 0" class="br-holiday">
            <span v-if="dayNote">📌 {{ dayNote }}</span>
            <span v-else>😴 美容師今日設定休假</span>
          </div>

          <!-- 有時段（可能全額滿） -->
          <div v-else class="br-slots">
            <button
              v-for="slot in filteredSlots"
              :key="slot.id"
              type="button"
              class="br-slot"
              :class="{
                'br-slot--selected': selectedSlotId === slot.id,
                'br-slot--full':     slot.available <= 0,
              }"
              :disabled="slot.available <= 0"
              @click="slot.available > 0 && (selectedSlotId = slot.id)"
            >
              <span class="br-slot__time">
                {{ formatSlotTime(slot.start_time) }}
                <template v-if="effectiveDuration && slot.available > 0">
                  &nbsp;–&nbsp;{{ calcEndTime(slot.start_time, effectiveDuration) }}
                </template>
              </span>
              <span v-if="slot.teacher_name" class="br-slot__teacher">
                {{ slot.teacher_name }}<template v-if="slot.service_item"> · {{ slot.service_item }}</template>
              </span>
              <span v-if="slot.available <= 0" class="br-slot__full">已額滿</span>
              <span v-else class="br-slot__avail">剩 {{ slot.available }} 位</span>
            </button>
          </div>
          <p v-if="errors.slot" class="br-error">{{ errors.slot }}</p>
        </section>

        <!-- Step 3：顧客資料 -->
        <section class="br-section">
          <h2 class="br-section-title">填寫資料</h2>

          <!-- LINE 登入區塊 -->
          <div class="br-line-login">
            <div v-if="lineUser" class="br-line-user">
              <img v-if="lineUser.picture" :src="lineUser.picture" class="br-line-user__avatar" />
              <span class="br-line-user__name">{{ lineUser.name }}</span>
              <button type="button" class="br-line-user__logout" @click="logoutLine">切換帳號</button>
            </div>
            <template v-else>
              <!-- 付費方案店家：必須 LINE 登入 -->
              <div v-if="lineRequired" class="br-line-required">
                <p class="br-line-required__title">🔒 此店家僅接受 LINE 登入預約</p>
                <p class="br-line-required__desc">請先用 LINE 登入，才能繼續填寫預約資料</p>
              </div>
              <button type="button" class="br-line-btn" @click="loginWithLine">
                <svg class="br-line-btn__icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.070 9.437-6.977C23.176 14.393 24 12.458 24 10.314"/>
                </svg>
                {{ lineRequired ? '用 LINE 登入以繼續預約' : '用 LINE 登入（不需填信箱）' }}
              </button>
            </template>
          </div>

          <div class="br-field" ref="formSectionRef">
            <label for="r-name">姓名 <span class="br-required">*</span></label>
            <input
              id="r-name"
              v-model.trim="form.customer_name"
              type="text"
              placeholder="請輸入姓名"
              class="br-input"
              :class="{ 'br-input--error': errors.customer_name }"
            />
            <p v-if="errors.customer_name" class="br-error">{{ errors.customer_name }}</p>
          </div>

          <div class="br-field">
            <label for="r-phone">手機號碼 <span class="br-required">*</span></label>
            <input
              id="r-phone"
              v-model.trim="form.customer_phone"
              type="tel"
              placeholder="09XXXXXXXX"
              class="br-input"
              :class="{ 'br-input--error': errors.customer_phone }"
            />
            <p v-if="errors.customer_phone" class="br-error">{{ errors.customer_phone }}</p>
          </div>

          <div class="br-field">
            <label for="r-email">
              Email
              <span v-if="!lineUser" class="br-required">*</span>
              <span v-else class="br-label-hint">（選填，填寫後可收到預約確認信）</span>
              <span v-if="!lineUser" class="br-label-hint">（用於接收預約確認信）</span>
            </label>
            <input
              id="r-email"
              v-model.trim="form.customer_email"
              type="email"
              placeholder="example@mail.com"
              class="br-input"
              :class="{ 'br-input--error': errors.customer_email }"
            />
            <p v-if="errors.customer_email" class="br-error">{{ errors.customer_email }}</p>
          </div>

          <div class="br-field">
            <label for="r-notes">備註（選填）</label>
            <textarea
              id="r-notes"
              v-model.trim="form.notes"
              rows="3"
              placeholder="如有特殊需求請填寫"
              class="br-input br-input--textarea"
            />
          </div>
        </section>

        <!-- API 錯誤訊息 -->
        <p v-if="apiError" class="br-api-error">{{ apiError }}</p>

        <!-- 2 小時截止提醒 -->
        <p class="br-hint-cutoff">
          ※ 線上預約須於預約時間 2 小時前完成，急需預約請直接聯絡店家。
        </p>


        <!-- 同意條款勾選 -->
        <label class="br-consent">
          <input type="checkbox" v-model="consentChecked" />
          <span>我已了解並同意：送出預約後，我所提供之姓名、電話及必要預約資訊，將提供予我所預約之店家，作為預約確認、聯繫及服務安排使用。</span>
        </label>

        <button
          type="submit"
          class="br-btn br-btn--primary br-btn--full"
          :disabled="submitDisabled"
        >
          <span v-if="submitting" class="br-spinner br-spinner--sm br-spinner--white" />
          {{ submitting ? '預約中…' : '下一步' }}
        </button>

      </form>

      <!-- 服務聲明 -->
      <BookingDisclaimer />

      <!-- 訪客查詢入口（懸浮按鈕） -->
      <BookingQueryFloat />
    </template>

  </main>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useBookingStore } from '@/store/bookingStore'
import BookingQueryFloat from '@/components/BookingQueryFloat.vue'
import BookingDisclaimer from '@/components/BookingDisclaimer.vue'

const route        = useRoute()
const router       = useRouter()
const bookingStore = useBookingStore()

// ── 路由參數 ────────────────────────────────────────
const storeId      = route.params.id
const serviceId    = route.query.serviceId    // 可能為 undefined → 顯示引導畫面
// 服務項目（從 BookingStore 傳入，可選）
const itemId       = route.query.itemId       // booking_service_items.id
const itemName     = route.query.itemName     // 服務項目名稱（e.g. 護膚）
const itemDuration = route.query.itemDuration // 服務時長（字串數字）
const itemPrice    = route.query.itemPrice    // 服務價格（字串數字）

// ── 服務選項（需等 store 載入後才有值）─────────────
const selectedService = computed(() =>
  bookingStore.currentServices.find(s => String(s.id) === String(serviceId))
)

// ── 有效時長：優先使用 item 的時長，否則用服務本身 ─
const effectiveDuration = computed(() =>
  itemDuration ? Number(itemDuration) : (selectedService.value?.duration_minutes ?? null)
)

// ── 日期：最小值 = 今天（Asia/Taipei）
// 不強制從明天起，與後端「slot 開始前 2 小時」規則一致。
// 今天仍可選，但 filteredSlots 會客戶端過濾掉 2 小時內的時段。
const minDate = computed(() =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' })
)

// ── 時段時間格式化：把 24:30 → 00:30，25:00 → 01:00（客戶顯示用）────
function resolveImg(url) {
  if (!url) return ''
  if (url.startsWith('/api/booking/img/') && process.env.NODE_ENV === 'development') {
    return `https://jiliskin.com${url}`
  }
  return url.replace(/^http:\/\/127\.0\.0\.1:\d+\/uploads\//, '/uploads/')
}

function formatSlotTime(t) {
  const [h, m] = t.split(':').map(Number)
  const dh = h % 24
  return `${String(dh).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// 依服務時長計算結束時間顯示（e.g. 08:00 + 90min → "09:30"）
// 跨日後套用 %24，讓凌晨顯示正確
function calcEndTime(startTimeStr, durationMins) {
  const [h, m] = startTimeStr.split(':').map(Number)
  const totalMins = h * 60 + m + Number(durationMins)
  const eh = Math.floor(totalMins / 60) % 24
  const em = totalMins % 60
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`
}

// 排序用：把 24:xx/25:xx 正規化到 0-3 的範圍，讓凌晨時段排在最前面
function slotSortKey(t) {
  const [h, m] = t.split(':').map(Number)
  return (h % 24) * 60 + m
}

// 把時段字串轉成 Date 物件（正確處理 ≥24h 的跨日時段）
function slotToDate(dateStr, timeStr) {
  const [h, m] = timeStr.split(':').map(Number)
  // 以當天 00:00 為基準，加上實際分鐘數
  const base = new Date(`${dateStr}T00:00:00+08:00`)
  base.setHours(base.getHours() + h, m, 0, 0)
  return base
}

// ── 過濾時段：今天的日期需額外排除距現在不足 2 小時的時段 ──
// 額滿時段仍保留（顯示 disabled），只排除 2 小時截止的時段。
// 最後依正規化時間排序（凌晨 00:xx 排最前面）。
const filteredSlots = computed(() => {
  const slots   = bookingStore.slots
  const todayTW = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' })

  let result = slots
  if (selectedDate.value === todayTW) {
    // cutoff = 現在 + 2 小時（已過截止的時段不顯示，即使額滿也不顯示）
    const cutoff = new Date(Date.now() + 2 * 60 * 60 * 1000)
    result = slots.filter(slot =>
      slotToDate(selectedDate.value, slot.start_time) > cutoff
    )
  }

  // 排序：00:xx（= 24:xx 跨日）→ 01:xx → … → 08:xx → 09:xx → …
  return [...result].sort((a, b) =>
    slotSortKey(a.start_time) - slotSortKey(b.start_time)
  )
})

// ── LINE 登入 ────────────────────────────────────────

const lineUser = ref(null)   // { name, userId, picture }
const consentChecked = ref(false)
const dateSectionRef = ref(null)
const formSectionRef = ref(null)

function loginWithLine() {
  // 儲存目前選擇的日期與時段，LINE 登入後還原（避免重選）
  if (selectedDate.value)   sessionStorage.setItem('br_savedDate',   selectedDate.value)
  if (selectedSlotId.value) sessionStorage.setItem('br_savedSlotId', String(selectedSlotId.value))
  // 透過 server 跳轉，避免 Android Chrome 將 access.line.me 交給 LINE App 處理
  const returnUrl = window.location.pathname + window.location.search
  window.location.href = `/api/auth/line/start?returnUrl=${encodeURIComponent(returnUrl)}`
}

function logoutLine() {
  lineUser.value = null
  // 清除 URL 中的 line 參數
  const url = new URL(window.location.href)
  url.searchParams.delete('line_name')
  url.searchParams.delete('line_user_id')
  url.searchParams.delete('line_picture')
  window.history.replaceState({}, '', url.toString())
}

// ── 狀態 ────────────────────────────────────────────
const selectedDate       = ref('')
const selectedSlotId     = ref(null)
const dayNote            = ref('')
const submitting          = ref(false)
const apiError            = ref('')
const pendingEmail        = ref(false)
const pendingEmailAddress = ref('')

// 此店家是否需要 LINE 登入才能預約（有填 Channel ID + Secret = 付費方案）
const lineRequired = computed(() => !!bookingStore.currentStore?.line_verify_enabled)

// 送出按鈕是否禁用
const submitDisabled = computed(() => {
  if (submitting.value) return true
  // 付費方案店家：必須 LINE 登入才能預約
  if (lineRequired.value && !lineUser.value) return true
  if (!consentChecked.value) return true
  return false
})


const form = ref({
  customer_name:  '',
  customer_phone: '',
  customer_email: '',
  notes:          '',
})

const errors = ref({
  slot:           '',
  customer_name:  '',
  customer_phone: '',
  customer_email: '',
})

// ── 初始化 ───────────────────────────────────────────
onMounted(async () => {
  // 處理 LINE 登入回調參數
  const urlParams = new URLSearchParams(window.location.search)
  const lineName    = urlParams.get('line_name')
  const lineUserId  = urlParams.get('line_user_id')
  const linePicture = urlParams.get('line_picture')
  if (lineName && lineUserId) {
    lineUser.value = { name: lineName, userId: lineUserId, picture: linePicture }
    form.value.customer_name = lineName
    // 清除 URL 中的 LINE 參數，保留其他 query
    const url = new URL(window.location.href)
    url.searchParams.delete('line_name')
    url.searchParams.delete('line_user_id')
    url.searchParams.delete('line_picture')
    window.history.replaceState({}, '', url.toString())

    // LINE 登入後：先確保 store 載入（selectedService 才有值），再還原日期時段
    const savedDate   = sessionStorage.getItem('br_savedDate')
    const savedSlotId = sessionStorage.getItem('br_savedSlotId')
    if (savedDate) {
      if (!bookingStore.currentStore) await bookingStore.fetchStore(storeId)
      selectedDate.value = savedDate
      sessionStorage.removeItem('br_savedDate')
      await onDateChange()
      // 還原時段選擇
      if (savedSlotId) {
        selectedSlotId.value = Number(savedSlotId)
        sessionStorage.removeItem('br_savedSlotId')
      }
      // 日期時段已還原，直接捲到填寫資料區，不需從頭重選
      await nextTick()
      formSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  if (!serviceId) return
  if (!bookingStore.currentStore) {
    await bookingStore.fetchStore(storeId)
  }

})

// ── 切換日期：重撈時段 + 載入備註 ───────────────────
async function onDateChange() {
  selectedSlotId.value = null
  errors.value.slot    = ''
  dayNote.value        = ''
  if (selectedDate.value) {
    await bookingStore.fetchSlots(
      storeId,
      selectedDate.value,
      effectiveDuration.value,
      selectedService.value?.name ?? null
    )
    // 載入當日備註
    try {
      const res = await fetch(`/api/booking/stores/${storeId}/day-note?date=${selectedDate.value}`)
      const json = await res.json()
      dayNote.value = json.note || ''
    } catch { dayNote.value = '' }
  }
}

// ── 表單驗證 ─────────────────────────────────────────
function validate() {
  let ok = true
  errors.value = { slot: '', customer_name: '', customer_phone: '', customer_email: '' }

  if (!selectedSlotId.value) {
    errors.value.slot = '請選擇預約時段'
    ok = false
  }
  if (!form.value.customer_name) {
    errors.value.customer_name = '請填寫姓名'
    ok = false
  }
  if (!form.value.customer_phone) {
    errors.value.customer_phone = '請填寫手機號碼'
    ok = false
  } else if (!/^09\d{8}$/.test(form.value.customer_phone)) {
    errors.value.customer_phone = '手機格式錯誤（需為 09 開頭 10 碼）'
    ok = false
  }
  // LINE 登入時 email 為選填
  if (!lineUser.value) {
    if (!form.value.customer_email) {
      errors.value.customer_email = '請填寫 Email'
      ok = false
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.customer_email)) {
      errors.value.customer_email = 'Email 格式錯誤'
      ok = false
    }
  } else if (form.value.customer_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.customer_email)) {
    errors.value.customer_email = 'Email 格式錯誤'
    ok = false
  }
  return ok
}

// ── 送出 ─────────────────────────────────────────────
async function submit() {
  apiError.value = ''
  if (!validate()) {
    // 有日期/時段錯誤時捲到該區塊
    if (errors.value.slot && dateSectionRef.value) {
      dateSectionRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    return
  }

  submitting.value = true
  try {
    const result = await bookingStore.createReservation({
      store_id:        Number(storeId),
      service_id:      Number(serviceId),
      service_item_id: itemId ? Number(itemId) : undefined,
      slot_id:         selectedSlotId.value,
      customer_name:   form.value.customer_name,
      customer_phone:  form.value.customer_phone,
      customer_email:  form.value.customer_email || undefined,
      notes:           form.value.notes || undefined,
      line_user_id:    lineUser.value?.userId || undefined,
    })

    if (result.success && result.pending_email) {
      // 後端已建立 pending_email 預約並寄出驗證信，等待用戶點擊連結
      pendingEmailAddress.value = form.value.customer_email
      pendingEmail.value        = true
    } else if (result.success) {
      const storeLineId = result.store_line_id
      // 取得選定時段的時間
      const selectedSlot = filteredSlots.value.find(s => s.id === selectedSlotId.value)
      const slotTime    = selectedSlot?.start_time ? selectedSlot.start_time.slice(0, 5) : ''
      // 用服務實際時長計算結束時間，而非 slot 本身的 end_time（固定 30 分鐘）
      const slotEndTime = (selectedSlot?.start_time && effectiveDuration.value)
        ? calcEndTime(selectedSlot.start_time.slice(0, 5), effectiveDuration.value)
        : (selectedSlot?.end_time ? selectedSlot.end_time.slice(0, 5) : '')
      router.push({
        name:  'BookingSuccess',
        query: {
          code:          result.reservation_code,
          is_member:     result.is_member ? '1' : '0',
          email:         form.value.customer_email,
          store_line_id: storeLineId || undefined,
          via_line:      lineUser.value ? '1' : undefined,
          slot_date:     selectedDate.value || undefined,
          slot_time:     slotTime           || undefined,
          slot_end_time: slotEndTime        || undefined,
          service_name:  selectedService.value?.name || undefined,
        },
      })
    } else {
      // 顯示後端回傳的錯誤訊息（包含「不足 2 小時」、「時段已額滿」等）
      apiError.value = result.message || '預約失敗，請稍後再試'
    }
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
/* 返回首頁列 */
.br-topbar { padding: 14px 0 0; margin-bottom: 12px; }
.br-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.88rem;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  background: #8b6f5c;
  border-radius: 999px;
  padding: 5px 14px;
  transition: background 0.15s;
}
.br-back-btn:hover { background: #7a5f4d; }

.booking-reserve {
  max-width: 640px;
  margin: 0 auto;
  padding: 24px 16px 64px;
}

/* 狀態 */
.br-state {
  text-align: center;
  padding: 64px 16px;
  color: #888;
}
.br-state--warn .br-state__title  { color: #e67e22; font-size: 1.2rem; margin-bottom: 8px; }
.br-state--error .br-state__title { color: #c0392b; font-size: 1.2rem; margin-bottom: 8px; }
.br-state__msg { margin-bottom: 24px; }
.br-state--inline {
  padding: 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}

/* 封面圖 */
.br-cover {
  position: relative;
  width: 100%;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
}
.br-cover__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  filter: brightness(0.85);
}
.br-cover__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 16px 20px;
  background: linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%);
}
.br-cover__name {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 1px 4px rgba(0,0,0,0.5);
}

/* 麵包屑 */
.br-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}
.br-breadcrumb a {
  display: inline-flex;
  align-items: center;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  text-decoration: none;
  background: #8b6f5c;
  border-radius: 999px;
  padding: 5px 14px;
  transition: background 0.15s;
}
.br-breadcrumb a:hover { background: #7a5f4d; }
.br-breadcrumb .br-sep { font-size: 0.8rem; color: #ddd; }
.br-breadcrumb .br-cur { font-size: 0.85rem; color: #aaa; }

/* 標題 */
.br-title {
  font-size: 1.6rem;
  color: #333;
  margin-bottom: 24px;
}

/* 服務摘要 */
.br-summary {
  background: #f9f6f3;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 28px;
}
.br-summary__label {
  font-size: 0.8rem;
  color: #aaa;
  margin: 0 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.br-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.br-summary__name {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}
.br-summary__meta {
  font-size: 0.88rem;
  color: #8b6f5c;
}

/* 區塊 */
.br-section {
  margin-bottom: 28px;
}
.br-section-title {
  font-size: 1rem;
  color: #555;
  border-bottom: 1px solid #ede7e2;
  padding-bottom: 8px;
  margin-bottom: 16px;
}

/* 日期 input */
.br-input--date {
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  max-width: 220px;
  background: #fff;
}

/* 提示 */
.br-hint {
  font-size: 0.9rem;
  color: #aaa;
  padding: 12px 0;
}
.br-hint--error {
  color: #c0392b;
  font-weight: 500;
}
.br-add-friend {
  background: #f0fff6;
  border: 1px solid #b2eac9;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.br-add-friend__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a7a40;
  margin: 0;
}
.br-add-friend__desc {
  font-size: 0.83rem;
  color: #4a7a5a;
  margin: 0;
}
.br-btn--add-friend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: #06c755;
  color: #fff;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s;
}
.br-btn--add-friend:hover { background: #05b34c; }
.br-add-friend__check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  color: #888;
  cursor: pointer;
}
.br-add-friend__check--active { color: #1a7a40; font-weight: 600; }
.br-add-friend__check input { cursor: pointer; }
.br-add-friend__hint {
  font-size: 0.8rem;
  color: #aaa;
  margin: 0;
}
.br-add-friend__hint--warn { color: #c0392b; }
.br-add-friend__verifying {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: #1a7a40;
  font-weight: 500;
}
.br-add-friend__verified {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a7a40;
  background: #d4f5e3;
  border-radius: 8px;
  padding: 10px 14px;
}
.br-btn--add-friend-sm {
  font-size: 0.85rem;
  padding: 9px 14px;
}
.br-spinner--green {
  border-color: rgba(6, 199, 85, 0.25);
  border-top-color: #06c755;
}
.br-hint-cutoff {
  font-size: 0.82rem;
  color: #aaa;
  margin-bottom: 20px;
  line-height: 1.5;
}

/* 時段格 */
.br-slots {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.br-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 18px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  font-size: 0.9rem;
}
.br-slot:hover { border-color: #8b6f5c; }
.br-slot--selected {
  border-color: #8b6f5c;
  background: #f5ede8;
}
.br-slot--full {
  border-color: #e0e0e0;
  background: #f9f9f9;
  cursor: not-allowed;
  opacity: 0.7;
}
.br-slot__time {
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}
.br-slot__teacher {
  font-size: 0.78rem;
  color: #6d4c41;
  font-weight: 500;
}
.br-slot__avail {
  font-size: 0.78rem;
  color: #aaa;
}
.br-slot__full {
  font-size: 0.78rem;
  color: #c0392b;
  font-weight: 500;
}

/* 休假提示 */
.br-holiday {
  font-size: 0.95rem;
  color: #888;
  padding: 16px 0;
  text-align: center;
}

/* 表單欄位 */
.br-field {
  margin-bottom: 20px;
}
.br-field label {
  display: block;
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 6px;
}
.br-required   { color: #c0392b; }
.br-label-hint { font-size: 0.82rem; color: #999; font-weight: 400; margin-left: 4px; }
.br-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.br-input:focus {
  outline: none;
  border-color: #8b6f5c;
}
.br-input--error { border-color: #c0392b; }
.br-input--textarea {
  resize: vertical;
  min-height: 80px;
}
.br-error {
  font-size: 0.82rem;
  color: #c0392b;
  margin-top: 4px;
}
.br-api-error {
  font-size: 0.9rem;
  color: #c0392b;
  background: #fdf0ef;
  border: 1px solid #f5c6c3;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  line-height: 1.5;
}

/* 按鈕 */
.br-btn {
  display: inline-block;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: background 0.15s;
}
.br-btn--primary {
  background: #8b6f5c;
  color: #fff;
}
.br-btn--primary:hover:not(:disabled) { background: #7a5f4d; }
.br-btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.br-btn--secondary {
  background: transparent;
  border: 1px solid #8b6f5c;
  color: #8b6f5c;
}
.br-btn--secondary:hover { background: #f5ede8; }
.br-btn--full { width: 100%; }

/* Spinner */
.br-spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid #ddd;
  border-top-color: #8b6f5c;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 12px;
}
.br-spinner--sm {
  width: 18px;
  height: 18px;
  border-width: 2px;
  margin-bottom: 0;
}
.br-spinner--white {
  border-color: rgba(255,255,255,0.3);
  border-top-color: #fff;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* 驗證等待畫面 */
.br-verify {
  text-align: center;
  padding: 56px 16px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.br-verify__icon {
  width: 64px;
  height: 52px;
  margin-bottom: 24px;
  color: #8b6f5c;
}
.br-verify__icon svg { width: 100%; height: 100%; }
.br-verify__title {
  font-size: 1.6rem;
  color: #333;
  margin: 0 0 12px;
}
.br-verify__email {
  font-size: 1.1rem;
  font-weight: 600;
  color: #8b6f5c;
  background: #f9f6f3;
  border: 1px solid #e8ddd7;
  border-radius: 8px;
  padding: 10px 20px;
  margin: 0 0 20px;
  word-break: break-all;
}
.br-verify__msg {
  font-size: 0.95rem;
  color: #555;
  line-height: 1.7;
  margin: 0 0 12px;
}
.br-verify__spam {
  background: #fff3e0;
  border: 2px solid #f0a94b;
  border-left: 6px solid #e8820c;
  border-radius: 12px;
  padding: 16px 18px;
  margin: 0 auto 32px;
  max-width: 440px;
  text-align: left;
  box-shadow: 0 2px 10px rgba(232, 130, 12, 0.12);
}
.br-verify__spam-title {
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 800;
  color: #c05a0c;
  line-height: 1.5;
}
.br-verify__spam-body {
  margin: 0;
  font-size: 0.95rem;
  color: #6b4a2b;
  line-height: 1.75;
}
.br-verify__spam-body b { color: #c05a0c; }
.br-actions-row {
  display: flex;
  gap: 12px;
  justify-content: center;
}

/* LINE 登入 */
.br-line-login {
  margin-bottom: 16px;
}
/* 付費方案：必須 LINE 登入提示 */
.br-line-required {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
}
.br-line-required__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px;
}
.br-line-required__desc {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
}
.br-line-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 18px;
  background: #06c755;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.br-line-btn:hover { background: #05b84d; }
.br-line-btn__icon { width: 22px; height: 22px; flex-shrink: 0; }
.br-line-user {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 10px 14px;
}
.br-line-user__avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  object-fit: cover;
}
.br-line-user__name {
  flex: 1;
  font-size: 0.92rem;
  font-weight: 600;
  color: #166534;
}
.br-line-user__logout {
  background: none;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.78rem;
  color: #166534;
  cursor: pointer;
}
.br-line-user__logout:hover { background: #dcfce7; }

/* 服務聲明 */
.br-consent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 16px 0 12px;
  font-size: 0.8rem;
  color: #555;
  line-height: 1.6;
  cursor: pointer;
}
.br-consent input[type="checkbox"] {
  flex-shrink: 0;
  margin-top: 3px;
  width: 16px;
  height: 16px;
  cursor: pointer;
}


@media (max-width: 480px) {
  .br-title { font-size: 1.3rem; }
  .br-slots { gap: 8px; }
  .br-slot  { padding: 8px 14px; }
}
</style>
